import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Calendar, Users, Loader2, Sparkles } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { WizardStepProps, CreateBookFormData } from '../../types';
import { UseFormReturn } from 'react-hook-form';
import { ageToHeightInches, formatInchesToHeight, cmToInches, inchesToCm } from '@/utils/height-utils';

interface MultiCharacterDetailsStepProps extends WizardStepProps {
  form: UseFormReturn<CreateBookFormData>;
  characterCount: number;
  isAnalyzing?: boolean;
}

// Removed AGE_OPTIONS - now using direct age input

const GENDER_OPTIONS = [
  { value: 'boy', label: 'Boy', icon: '👦' },
  { value: 'girl', label: 'Girl', icon: '👧' },
  { value: 'neutral', label: 'Neutral', icon: '🧒' },
];

export function MultiCharacterDetailsStep({ 
  onNext, 
  onBack, 
  form,
  characterCount,
  isAnalyzing = false
}: MultiCharacterDetailsStepProps) {
  console.log('[WIZARD] MultiCharacterDetailsStep MOUNTED with props:', {
    characterCount,
    hasForm: !!form,
    hasOnNext: !!onNext,
    hasOnBack: !!onBack
  });
  
  try {
    const [activeTab, setActiveTab] = useState(0);
    const [heightUnit, setHeightUnit] = useState<'in' | 'cm'>('in');
    const [heightText, setHeightText] = useState(''); // free-typed text for the height input (canonical stored on blur)
    const HEIGHT_MIN_IN = 20, HEIGHT_MAX_IN = 90;
    const mainCharacterIndex = form.watch('mainCharacterIndex');
    
    // Debug logging
    console.log('[WIZARD] MultiCharacterDetailsStep render:', {
      characterCount,
      formCharacters: form?.watch('characters'),
      formCharacterCount: form?.watch('characterCount'),
      mainCharacterIndex,
      hasOnNext: !!onNext,
      hasOnBack: !!onBack,
      hasForm: !!form
    });
    
    // Check if required props are missing
    if (!form) {
      console.error('[WIZARD] MultiCharacterDetailsStep: form prop is missing');
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-2">
            <p className="text-red-600">Error: Form not initialized</p>
          </div>
        </div>
      );
    }
    
    // Get current values from form
    const charactersData = form.watch('characters') || [];
  
    // Initialize characters array if needed
    React.useEffect(() => {
      console.log('[WIZARD] MultiCharacterDetailsStep useEffect:', {
        charactersDataLength: charactersData.length,
        characterCount
      });
      
      if (charactersData.length < characterCount) {
        const newCharacters = Array(characterCount).fill(null).map((_, index) => ({
          ...charactersData[index] || {
            name: '',
            age: '',
            gender: '',
            skinTone: '',
            hairColor: '',
            eyeColor: '',
            hairTexture: '',
            heightInches: '',
            heightUserSet: false,
            bbox: charactersData[index]?.bbox || undefined,
            thumbnail: charactersData[index]?.thumbnail || undefined
          }
        }));
        form.setValue('characters', newCharacters);
        console.log('[WIZARD] Initialized characters in MultiCharacterDetailsStep:', newCharacters);
      }
    }, [characterCount, form, charactersData]);

    // Backfill the height guess for characters whose age is already set (e.g. pre-filled by photo
    // analysis before this step mounted) but whose height hasn't been guessed and the user hasn't set it.
    // Idempotent: only fills empty heights, so it settles after one pass (no loop).
    React.useEffect(() => {
      let changed = false;
      const updated = charactersData.map((c: any) => {
        if (c && !c.heightUserSet && (c.heightInches === '' || c.heightInches == null) && c.age) {
          const ageNum = parseInt(String(c.age), 10);
          if (ageNum > 0) { changed = true; return { ...c, heightInches: String(ageToHeightInches(ageNum, undefined, c.gender)) }; }
        }
        return c;
      });
      if (changed) form.setValue('characters', updated);
    }, [charactersData, form]);

    // Keep the height input's displayed text in sync with the canonical value (per active tab + unit).
    // heightInches only changes on blur/derive (not per keystroke), so this never clobbers active typing.
    React.useEffect(() => {
      const ch = charactersData[activeTab];
      const inches = parseInt(String(ch?.heightInches ?? ''), 10);
      setHeightText(Number.isFinite(inches) && inches > 0
        ? (heightUnit === 'cm' ? String(inchesToCm(inches)) : String(inches))
        : '');
    }, [activeTab, heightUnit, charactersData]);

    // Ensure we have valid character data before rendering
    if (!characterCount || characterCount === 0) {
      console.log('[WIZARD] MultiCharacterDetailsStep waiting for initialization:', {
        characterCount,
        charactersDataLength: charactersData.length
      });
      return (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center justify-center py-12"
        >
          <div className="text-center space-y-2">
            <User className="w-12 h-12 mx-auto text-gray-400" />
            <p className="text-gray-600">Setting up character details...</p>
          </div>
        </motion.div>
      );
    }

    const handleInputChange = (field: keyof CreateBookFormData['characters'][0], value: string) => {
      const updatedCharacters = [...charactersData];
      if (!updatedCharacters[activeTab]) {
        updatedCharacters[activeTab] = {
          name: '',
          age: '',
          gender: '',
          skinTone: '',
          hairColor: '',
          eyeColor: '',
          hairTexture: '',
          heightInches: '',
          heightUserSet: false,
          bbox: undefined,
          thumbnail: undefined
        };
      }
      updatedCharacters[activeTab][field] = value;
      form.setValue('characters', updatedCharacters);
    };

    // When age changes, re-guess height (gender-aware) UNLESS the user has manually set it.
    const handleAgeChange = (value: string) => {
      const updated = [...charactersData];
      if (!updated[activeTab]) return handleInputChange('age', value);
      updated[activeTab] = { ...updated[activeTab], age: value };
      if (!updated[activeTab].heightUserSet) {
        const ageNum = parseInt(value, 10);
        updated[activeTab].heightInches = ageNum > 0 ? String(ageToHeightInches(ageNum, undefined, updated[activeTab].gender)) : '';
      }
      form.setValue('characters', updated);
    };

    // When gender changes, re-guess height (gender-aware) UNLESS the user has manually set it.
    const handleGenderChange = (value: string) => {
      const updated = [...charactersData];
      if (!updated[activeTab]) return handleInputChange('gender', value);
      updated[activeTab] = { ...updated[activeTab], gender: value };
      if (!updated[activeTab].heightUserSet) {
        const ageNum = parseInt(String(updated[activeTab].age), 10);
        updated[activeTab].heightInches = ageNum > 0 ? String(ageToHeightInches(ageNum, undefined, value)) : '';
      }
      form.setValue('characters', updated);
    };

    // Height: type freely (no per-keystroke clamping); normalize + persist on blur. The displayed text
    // lives in `heightText`; only on blur do we convert (cm->in), clamp, store canonical inches, and lock.
    const handleHeightBlur = () => {
      const updated = [...charactersData];
      if (!updated[activeTab]) return;
      const num = parseInt(heightText, 10);
      let stored = '';
      if (Number.isFinite(num)) {
        let inches = heightUnit === 'cm' ? cmToInches(num) : num;
        inches = Math.max(HEIGHT_MIN_IN, Math.min(HEIGHT_MAX_IN, inches));
        stored = String(inches);
      }
      updated[activeTab] = { ...updated[activeTab], heightInches: stored, heightUserSet: true };
      form.setValue('characters', updated); // sync effect re-renders heightText from the normalized value
    };

    const isStepValid = () => {
      return charactersData.length === characterCount &&
        charactersData.every(char => char.age && char.gender);
    };

    const currentCharacter = charactersData[activeTab] || {};

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* AI Analysis Loading Indicator */}
        {isAnalyzing && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3"
          >
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">Analyzing your photo...</p>
              <p className="text-xs text-blue-600">Pre-filling age and gender based on the photo</p>
            </div>
            <Sparkles className="w-5 h-5 text-blue-600" />
          </motion.div>
        )}

        {/* IMPROVEMENT 3: AI Analysis Failure Warning */}
        {!isAnalyzing && charactersData.some((char: any) => char.aiConfidence === 0) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-2xl">⚠️</div>
              <div className="flex-1">
                <p className="text-sm font-bold text-yellow-900">Analysis Failed</p>
                <p className="text-xs text-yellow-800 mt-1">
                  We couldn't analyze the photo after multiple attempts. The ages and genders shown are rough estimates.
                  <strong className="block mt-1">Please review and correct all character details carefully!</strong>
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* IMPROVEMENT 5: Low Confidence Warning */}
        {!isAnalyzing && !charactersData.some((char: any) => char.aiConfidence === 0) &&
         charactersData.some((char: any) => char.aiConfidence && char.aiConfidence < 0.7) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-50 border border-blue-300 rounded-lg p-3"
          >
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0">ℹ️</div>
              <p className="text-xs text-blue-800">
                Detection confidence is moderate. Please verify the pre-filled information is accurate.
              </p>
            </div>
          </motion.div>
        )}

        {/* Tab navigation with thumbnails */}
        <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
          {Array(characterCount).fill(null).map((_, index) => {
            const character = charactersData[index];
            return (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`flex-1 py-2 px-3 rounded-md transition-all text-sm font-medium flex items-center gap-2 ${
                  activeTab === index 
                    ? 'bg-white shadow-sm text-primary' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {character?.thumbnail ? (
                  <img 
                    src={character.thumbnail} 
                    alt={`Person ${index + 1}`}
                    className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-200">
                    <User className="w-4 h-4 text-gray-400" />
                  </div>
                )}
                <span className="flex-1 text-left">
                  {character?.name || `Person ${index + 1}`}
                </span>
                {index === mainCharacterIndex && (
                  <Badge variant="default" className="ml-1 text-xs bg-green-600">Main</Badge>
                )}
              </button>
            );
          })}
        </div>

        {/* Character form */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {/* Character thumbnail header */}
            {currentCharacter.thumbnail && (
              <div className="flex justify-center">
                <img 
                  src={currentCharacter.thumbnail} 
                  alt={currentCharacter.name || `Person ${activeTab + 1}`}
                  className="w-24 h-24 rounded-full object-cover border-4 shadow-lg border-white"
                />
              </div>
            )}
            
            {/* Name display (read-only since it comes from face detection) */}
            <div className="space-y-2">
              <Label className="text-base font-medium">
                Name
              </Label>
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-lg font-medium">
                {currentCharacter.name || `Person ${activeTab + 1}`}
              </div>
            </div>

            {/* Age input */}
            <div className="space-y-3">
              <Label htmlFor={`age-${activeTab}`} className="text-base font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Age (in years)
              </Label>
              <Input
                id={`age-${activeTab}`}
                type="number"
                min="1"
                max="120"
                value={currentCharacter.age || ''}
                onChange={(e) => handleAgeChange(e.target.value)}
                placeholder="Enter age (e.g., 7)"
                className="text-lg"
              />
              <p className="text-sm text-gray-500">
                Enter the exact age (e.g., 7, not a range like 5-7)
              </p>
            </div>

            {/* Height input (guessed from age, editable) */}
            <div className="space-y-3">
              <Label htmlFor={`height-${activeTab}`} className="text-base font-medium">Height</Label>
              <div className="flex items-center gap-2">
                <Input
                  id={`height-${activeTab}`}
                  type="number"
                  min={heightUnit === 'cm' ? '50' : '20'}
                  max={heightUnit === 'cm' ? '230' : '90'}
                  value={heightText}
                  onChange={(e) => setHeightText(e.target.value)}
                  onBlur={handleHeightBlur}
                  placeholder={heightUnit === 'cm' ? 'e.g. 170' : 'e.g. 67'}
                  className="text-lg"
                />
                <select
                  aria-label="height unit"
                  value={heightUnit}
                  onChange={(e) => setHeightUnit(e.target.value as 'in' | 'cm')}
                  className="border rounded-lg px-3 py-2 text-lg"
                >
                  <option value="in">in</option>
                  <option value="cm">cm</option>
                </select>
              </div>
              <p className="text-sm text-gray-500">
                {(() => {
                  const inches = parseInt(String(currentCharacter.heightInches || ''), 10);
                  return Number.isFinite(inches)
                    ? `≈ ${formatInchesToHeight(inches)} (estimated from age — adjust if needed)`
                    : 'Estimated from age — adjust if needed';
                })()}
              </p>
            </div>

            {/* Gender selection */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Gender</Label>
              <div className="grid grid-cols-3 gap-3">
                {GENDER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleGenderChange(option.value)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      currentCharacter.gender === option.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{option.icon}</div>
                    <div className="text-sm font-medium">{option.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation hint */}
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
          <span>Character {activeTab + 1} of {characterCount}</span>
          {activeTab < characterCount - 1 && (
            <>
              <span>•</span>
              <button
                onClick={() => setActiveTab(activeTab + 1)}
                className="text-primary hover:underline"
              >
                Next character →
              </button>
            </>
          )}
        </div>

        {/* Hidden relationship field */}
        <input
          type="hidden"
          {...form.register('relationshipType')}
          value={characterCount === 2 ? 'siblings' : 'friends'}
        />
      </motion.div>
    );
  } catch (error) {
    console.error('[WIZARD] Error in MultiCharacterDetailsStep:', error);
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-2">
          <p className="text-red-600">Error rendering character details</p>
          <p className="text-gray-600 text-sm">{String(error)}</p>
        </div>
      </div>
    );
  }
}
